<template>
  <div class="wrapper" :class="{'sidebar-shown': displaySidebar, 'sidebar-open': sidebarOpen}">
    <sidebar active="3" @openSidebar="sidebarOpen = true" @closeSidebar="sidebarOpen = false" @saveProgress="showSaveProgress = true"/>
    <main id="main" class="content has-sidebar has-totals">
      <app-header :back="back"></app-header>
      <header class="page">
        <h1 class="title">Select your species</h1>
        <p class="description">Read our <em>Common Sense Mix Tips</em> to easily build a mix that falls within your price range.</p>
        <button class="btn btn-primary" @click.prevent="showModal = true">Read Mix Tips</button>
      </header>
      <div v-show="canDisplaySpecies" class="crop-groups">
        <crop-group
            v-for="group in groups"
            v-bind:key="group.label"
            v-bind:group="group"
            v-bind:activeCrop="activeCrop"
            @selected="handleSelected"
            @removed="handleCropRemoved"></crop-group>
      </div>
      <div v-show="!canDisplaySpecies" class="alert">
        We don't have enough information to make recommendations. Please use the back button or <router-link to="/start">start over</router-link> and fill in all the required information.
      </div>
    </main>
    <info-bar
      v-if="activeCrop"
      v-bind:crop="activeCrop"
      v-bind:amount="activeAmount"
      v-bind:groups="groups"
      v-bind:rating="activeRating"
      @close="handleInfobarClose"
      @added="handleCropAdded"
      @removed="handleCropRemoved"
      @cropChange="setNewCrop"></info-bar>
    <totals-bar
      @next="$router.push({ name: 'inoculation' })"
      @edit="handleSelected"
      @remove="handleCropRemoved"
      @reset="$emit('resetCrops')"></totals-bar>
    <modal @close="showModal = false" name="Mix Tips" :shown="showModal">
      <p>Using average price per acre will allow you to easily build a mix that falls within your general price range.</p>
      <p>Species with high seeding rates will add the most overall cost to the mix. Using small seeded species helps to fulfill seeding rate requirements at a lower price.</p>
      <p>Species with high cost can be effectively used at smaller percentages in combination with lower cost species to hit your price goal.</p>
    </modal>
    <modal @close="handleInstructionsClose" :name="'Mixing for ' + this.$store.state.plantingMethods[this.$store.state.formData.plantingMethod]" :shown="showInitalModal">
      <p>Add species to build your mix. For {{ this.$store.state.plantingMethods[this.$store.state.formData.plantingMethod] }}, try to keep your full seeding rate around {{ this.$store.state.mixTargets[this.$store.state.formData.plantingMethod] }}%.</p>
      <button class="btn btn-primary has-arrow" @click.prevent="handleInstructionsClose">Got it, let's start mixing</button>
    </modal>
    <save-progress :shown="showSaveProgress" @close="showSaveProgress = false" />
  </div>
</template>

<script>
import Sidebar from '@/components/Sidebar'
import TotalsBar from '@/components/TotalsBar'
import Infobar from '@/components/Infobar'
import Header from '@/components/Header'
import Modal from '@/components/Modal'
import CropGroup from '@/components/CropGroup'
import SaveProgress from '@/components/SaveProgress'
export default {
  components: {
    'sidebar': Sidebar,
    'totals-bar': TotalsBar,
    'info-bar': Infobar,
    'app-header': Header,
    'modal': Modal,
    'crop-group': CropGroup,
    'save-progress': SaveProgress
  },
  data () {
    return {
      back: 'goals',
      displaySidebar: true,
      sidebarOpen: false,
      activeCrop: null,
      activeRating: null,
      activeAmount: 0,
      showInfoBar: false,
      showModal: false,
      showInitalModal: this.$store.state.showMixInstructions,
      showSaveProgress: false
    }
  },
  methods: {
    handleInstructionsClose () {
      this.showInitalModal = false
      this.$store.commit('showMixInstructions', false)
    },
    handleInfobarClose () {
      this.showInfoBar = false
      this.activeCrop = null
    },
    isCropInMix () {
      let isInMix = false
      this.$store.state.formData.selectedCrops.forEach((entry, index) => {
        if (entry.crop === this.activeCrop) {
          isInMix = true
        }
      })
      return isInMix
    },
    getAmount () {
      let amount = ''
      if (this.isCropInMix()) {
        this.$store.state.formData.selectedCrops.forEach((entry, index) => {
          if (entry.crop === this.activeCrop) {
            amount = entry.amount // get the saved amount as lbs/acre
          }
        })
        if (this.$store.state.formData.mixUnits === '%') {
          amount = amount / this.activeCrop['seedingRate'] * 100 // convert to percentage
        }
      }
      return amount
    },
    handleSelected (data) {
      this.setNewCrop(data)
      this.showInfoBar = true
    },
    setNewCrop (data) {
      this.activeCrop = data.crop
      this.activeRating = data.rating
      this.activeAmount = this.getAmount()
    },
    handleCropAdded () {
      this.activeAmount = this.getAmount()
      this.$emit('updateTotals') // bubble up to App.vue
    },
    handleCropRemoved (crop) {
      this.activeAmount = ''
      this.$emit('cropRemoved', crop) // bubble up to App.vue
    }
  },
  computed: {
    canDisplaySpecies () {
      let canDisplay = true
      if (!this.$store.state.formData.plantingTime) {
        canDisplay = false
      }
      return canDisplay
    },
    recommendations () {
      let recommendations = {
        'excellent': [],
        'fair': [],
        'poor': []
      }
      this.$store.state.availableCrops.forEach((crop, index) => {
        let recommendedGoals = crop['recGoals']
        let primaryGoal = this.$store.state.formData.primaryGoal + 1 // selections are 0 indexed, data is not
        let secondaryGoal = this.$store.state.formData.secondaryGoal + 1 // selections are 0 indexed, data is not
        if (crop['recPlantingTimes'].includes(this.$store.state.formData.plantingTime)) {
          if ((recommendedGoals.includes(primaryGoal) || recommendedGoals.includes(secondaryGoal))) {
            // crop matches recommended time and either goal
            recommendations.excellent.push(crop)
          } else {
            // crop matches recommended time but not the goals
            recommendations.poor.push(crop)
          }
        } else if (crop['suitablePlantingTimes'].includes(this.$store.state.formData.plantingTime)) {
          if ((recommendedGoals.includes(primaryGoal) || recommendedGoals.includes(secondaryGoal))) {
            // crop matches suitable time and either goal
            recommendations.fair.push(crop)
          } else {
            // crop matches suitable time but not the goals
            recommendations.poor.push(crop)
          }
        }
      })
      return recommendations
    },
    groups () {
      return [
        { rating: 5, label: 'excellent', title: 'Recommended for planting time and goals', items: this.recommendations.excellent },
        { rating: 3, label: 'fair', title: 'Suitable for planting time and goals', items: this.recommendations.fair },
        { rating: 1, label: 'poor', title: 'Suitable for planting time, but not goals', items: this.recommendations.poor }
      ]
    }
  }
}
</script>

<style lang="scss">
.crop-groups{
  padding-top: 30px;
  margin-bottom: 100px;
  @include media-breakpoint-up(lg){
    padding-top: 100px;
  }
}
</style>
